import SimpleVast from '../index';

export function playLinearAd(creative) {
  // The OMID verification path reaches here from a setTimeout / script-load callback, so it can
  // fire after the plugin was disposed. Without this the ad video is created and plays its audio
  // from a detached element.
  if (this._disposed) return;
  this.debug('playLinearAd', creative);

  const mediaFile = SimpleVast.getBestMediaFile(creative.mediaFiles, this.options.getContentQuality?.());
  if (!mediaFile) {
    this.debug('playLinearAd: no suitable media file found');
    this._captureError('SimpleVast: No suitable media file found', {
      mediaFiles: creative.mediaFiles?.map((m) => ({
        url: m.fileURL, mimeType: m.mimeType, width: m.width, height: m.height,
      })) ?? null,
    });
    this._resumeContent();
    return;
  }

  if (!this.isInAdMode) {
    this._enterAdMode();
  }

  this._trigger('playAttempt');

  // Start the load timeout — reset it once the browser actually starts fetching
  // the ad media (loadstart), so slow connections get the full window after the
  // network request begins rather than counting down during DNS/connect overhead.
  const startTimeout = () => {
    clearTimeout(this._adLoadTimeout);
    this._adLoadTimeout = setTimeout(() => this.onAdTimeout(), this.options.timeout);
  };
  startTimeout();
  this.adVideo.addEventListener('loadstart', startTimeout, { once: true });

  // Token for this play attempt so a stale handler from a superseded ad can't act on the
  // wrong one.
  this._adPlayToken = (this._adPlayToken || 0) + 1;
  const playToken = this._adPlayToken;

  this.adVideo.src = mediaFile.fileURL;
  this.adVideo.load();
  this.adVideo.play().catch((err) => {
    if (playToken !== this._adPlayToken) return; // superseded by a newer ad attempt
    this.debug('playLinearAd: play() rejected', err);
    // Some TV browsers (e.g. Samsung Tizen) reject play() with NotSupportedError on a failed
    // src (404) WITHOUT ever firing the element's 'error' event — that rejection is the only
    // failure signal we get, so treat it as a media error and skip the ad.
    if (err?.name === 'NotSupportedError' || this.adVideo.error) {
      this.adVideo.removeEventListener('loadstart', startTimeout);
      clearTimeout(this._adLoadTimeout);
      this._adLoadTimeout = null;
      this._lastPlayRejection = err; // included in onAdError's Sentry report
      this.onAdError();
      return;
    }
    // Other media failures are handled by onerror above. The only case left to handle here is
    // a genuine autoplay block (a user gesture is required).
    if (!err || err.name !== 'NotAllowedError') return;
    this.adVideo.removeEventListener('loadstart', startTimeout);
    clearTimeout(this._adLoadTimeout);
    this._adLoadTimeout = null;
    // Show controls so the viewer can start the ad by hand.
    const offerManualStart = () => {
      // _adStartFired stays false so tracking beacons still fire on actual play.
      if (this._adStartFired) return;
      this._trigger('play', {
        ctaUrl: this.ctaUrl,
        skipDelay: this.linearVastTracker?.skipDelay,
        adClickCallback: this.ctaUrl ? () => this.adClickCallback(this.ctaUrl) : false,
      });
    };
    // The break is the first thing the browser is asked to start, so its refusal is the answer
    // for the whole session: mute and retry, and let UnmuteNotice ask for the click that undoes
    // it. Only when even a muted start is refused does the manual one remain.
    if (!this._retryPlaybackMuted(this.adVideo, offerManualStart)) offerManualStart();
  });

  this.setMacros({
    ASSETURI: mediaFile.fileURL,
    ADPLAYHEAD: this.linearVastTracker.convertToTimecode(0),
    CONTENTPLAYHEAD: this.linearVastTracker.convertToTimecode(this.video.currentTime),
  });
}
